import React, { Fragment, useState, useRef, useEffect } from 'react';
import { DirectUpload } from '@rails/activestorage';
import { Form, ProgressBar, Icon, P, BlankLink } from '@contently/toolkit';
import _ from 'underscore';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import isValid, { VALID_FILE_TYPES } from '../../utils/fileUploadValidator';

const UploadedFile = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: start;
`;

function UploadProgressBar({ progress }) {
  return progress ? <ProgressBar percent={progress} status="active" /> : null;
}

export default function DirectAttachmentUpload(props) {
  const { id, onUploadBegin, onUploadSuccess, onUploadError, onRemoveFile, file } = props;
  const [uploadError, setUploadError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputEl = useRef(null);

  const handleUpload = async uploadFile => {
    const upload = new DirectUpload(uploadFile, '/rails/active_storage/direct_uploads');

    // Add event listeners to the form/input for direct upload events
    const input = inputEl.current;

    input.addEventListener('direct-upload:progress', event => {
      const { progress } = event.detail;
      setUploadProgress(progress);
    });

    input.addEventListener('direct-upload:error', event => {
      const { error } = event.detail;
      handleUploadError(error);
    });

    try {
      const blob = await new Promise((resolve, reject) => {
        upload.create((error, blob) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(blob);
        });
      });

      onUploadSuccess({
        name: uploadFile.name,
        signedId: blob.signed_id
      });
      setUploadProgress(0);
    } catch (error) {
      handleUploadError(error.message || 'Upload failed');
    }
  };

  const handleRemoveFile = () => {
    if (inputEl.current) {
      inputEl.current.value = '';
    }
    setUploadError(false);
    setUploadProgress(0);
    onRemoveFile();
  };

  const handleUploadError = msg => {
    if (inputEl.current) {
      inputEl.current.value = '';
    }
    setUploadError(true);
    setUploadProgress(0);
    onUploadError(msg);
  };

  const handleFileChange = e => {
    const tmpFile = e.currentTarget.files[0];
    if (!tmpFile) return;

    const valid = isValid(tmpFile, handleUploadError, handleUploadError);
    if (!valid) return;

    onUploadBegin(tmpFile);
    handleUpload(tmpFile);
  };

  useEffect(() => {
    const currentInput = inputEl.current;

    const handleProgress = event => {
      const { progress } = event.detail;
      setUploadProgress(progress);
    };

    const handleError = event => {
      const { error } = event.detail;
      handleUploadError(error);
    };

    if (currentInput) {
      currentInput.addEventListener('direct-upload:progress', handleProgress);
      currentInput.addEventListener('direct-upload:error', handleError);

      return () => {
        currentInput.removeEventListener('direct-upload:progress', handleProgress);
        currentInput.removeEventListener('direct-upload:error', handleError);
      };
    }
  }, []);

  return (
    <Fragment>
      <div className="input-uploader">
        <Form.Button variant="secondary" size="small">
          Upload
        </Form.Button>
        <Form.Input
          id={id}
          name={id}
          type="file"
          accept={VALID_FILE_TYPES.join(',')}
          ref={inputEl}
          onChange={handleFileChange}
        />
      </div>

      <UploadProgressBar progress={uploadProgress} />

      {file && !_.isEmpty(file.name) && (
        <UploadedFile>
          <P>
            {file.url ? (
              <BlankLink title="Uploaded file" href={file.url}>
                {file.name}
              </BlankLink>
            ) : (
              <span>{file.name}</span>
            )}
            {uploadError}
          </P>
          <Icon type="x" onClick={handleRemoveFile} withTextLeft />
        </UploadedFile>
      )}
    </Fragment>
  );
}

export function useDirectAttachmentUploadFormik({ setFieldValue, setErrors, values }) {
  const [isUploading, setIsUploading] = useState(false);

  const onUploadBegin = file => {
    setIsUploading(true);
    setFieldValue('file', { name: file.name });
  };

  const onUploadSuccess = ({ name, signedId }) => {
    setIsUploading(false);
    setFieldValue('file', {
      name,
      signedId
    });
  };

  const onRemoveFile = () => {
    setFieldValue('file', null);
  };

  const onUploadError = error => setErrors({ file: error });

  return [
    isUploading,
    {
      onUploadBegin,
      onUploadSuccess,
      onRemoveFile,
      onUploadError,
      file: values.file
    }
  ];
}

DirectAttachmentUpload.propTypes = {
  onUploadBegin: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  onUploadError: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string
  })
};

DirectAttachmentUpload.defaultProps = {
  file: { name: '', url: '' }
};



// WEBPACK FOOTER //
// ./app/javascript/app/components/forms/active_storage_direct_uploader.jsx